/**
 * 3D world scene-graph store.
 *
 * Mirrors the shape of the timeline's useEditorStore but for a multi-asset
 * Three.js scene. Generator chat agents drive it through window.protoworld.*
 * (see ../lib/protoworld.ts). The renderer is in ../components/ThreeDViewer.tsx (WorldAsset)
 * maps this store into JSX inside a single <Canvas>.
 *
 * Designed for low-resource mobile: zustand, no middleware, no devtools,
 * shallow Float32 transforms only.
 */
import { create } from 'zustand';

export type Vec3 = [number, number, number];

export interface SceneAsset {
  id: string;
  /**
   * Asset source. Three shapes:
   *   • `https://…/foo.glb` — fetched and parsed by GLTFLoader
   *   • `data:model/obj;base64,…` — inline OBJ (used by monster-mash extrude)
   *   • `proto://shape/<name>` — primitive generated in-place (cube, sphere, torus, …)
   */
  url: string;
  /** Display label. */
  name: string;
  position: Vec3;
  rotation: Vec3;
  scale: Vec3;
  /** Optional tag for agent queries ("tree", "character", "prop"). */
  tag?: string;
  /** Texture URL (preferred over color), applied to the mesh's material.map. */
  texture?: string;
  /** Material kind. Implemented in WorldAsset renderer. */
  material?: 'standard' | 'distort' | 'wobble' | 'edges' | 'wireframed';
  /** Base color (when no texture is set). */
  color?: string;
  /** Animation kind read by useFrame in the renderer. */
  animation?: 'spin' | 'bounce' | 'pulse' | 'orbit' | 'float' | 'shake' | 'swing' | 'sway' | 'jiggle' | 'none';
  /** Speed multiplier for the animation (1 = baseline). */
  animationSpeed?: number;
  /** For `proto://shape/text` — text content + font URL. */
  text?: string;
  font?: string;
}

export interface SceneLight {
  id: string;
  type: 'ambient' | 'directional' | 'point';
  color: string;          // hex
  intensity: number;
  position?: Vec3;        // unused for ambient
}

export interface SceneEnvironment {
  preset: 'apartment' | 'city' | 'dawn' | 'forest' | 'lobby' | 'night' | 'park' | 'studio' | 'sunset' | 'warehouse' | null;
  background: string | null;       // CSS color, hex, or HDR url
  fog?: { color: string; near: number; far: number } | null;
}

interface SceneState {
  assets: SceneAsset[];
  lights: SceneLight[];
  environment: SceneEnvironment;
  selectedAssetId: string | null;

  /** url + name are required; everything else has sensible defaults. */
  addAsset(a: { id?: string; url: string; name: string } & Partial<Omit<SceneAsset, 'id' | 'url' | 'name'>>): string;
  updateAsset(id: string, patch: Partial<SceneAsset>): void;
  removeAsset(id: string): void;
  setSelected(id: string | null): void;

  addLight(l: Omit<SceneLight, 'id'> & { id?: string }): string;
  removeLight(id: string): void;

  setEnvironment(patch: Partial<SceneEnvironment>): void;
  clear(): void;
}

function uid() { return Math.random().toString(36).slice(2, 10); }

export const useSceneStore = create<SceneState>((set) => ({
  assets: [],
  lights: [
    { id: 'L-ambient', type: 'ambient', color: '#ffffff', intensity: 0.4 },
    { id: 'L-key',     type: 'directional', color: '#ffffff', intensity: 1.0, position: [5, 10, 5] },
  ],
  environment: { preset: 'city', background: null, fog: null },
  selectedAssetId: null,

  addAsset: (a) => {
    const id = a.id ?? uid();
    set((s) => ({
      assets: [...s.assets, {
        id,
        url: a.url,
        name: a.name,
        position: a.position ?? [0, 0, 0],
        rotation: a.rotation ?? [0, 0, 0],
        scale: a.scale ?? [1, 1, 1],
        tag: a.tag,
        texture: a.texture,
        material: a.material,
        color: a.color,
        animation: a.animation,
        animationSpeed: a.animationSpeed,
        text: a.text,
        font: a.font,
      }],
    }));
    return id;
  },

  updateAsset: (id, patch) => set((s) => ({
    assets: s.assets.map((x) => (x.id === id ? { ...x, ...patch } : x)),
  })),

  removeAsset: (id) => set((s) => ({
    assets: s.assets.filter((x) => x.id !== id),
    selectedAssetId: s.selectedAssetId === id ? null : s.selectedAssetId,
  })),

  setSelected: (id) => set({ selectedAssetId: id }),

  addLight: (l) => {
    const id = l.id ?? `L-${uid()}`;
    set((s) => ({ lights: [...s.lights, { ...l, id } as SceneLight] }));
    return id;
  },

  removeLight: (id) => set((s) => ({ lights: s.lights.filter((x) => x.id !== id) })),

  setEnvironment: (patch) => set((s) => ({ environment: { ...s.environment, ...patch } })),

  clear: () => set({ assets: [], selectedAssetId: null }),
}));
